import { DuviNicheSiteIcon } from "@/components/duvi-niche-site-icon";
import type { DuviNicheSiteIconName } from "@/components/duvi-niche-site-icon";

export interface DuviNicheSiteFeatureCardProps {
  icon: DuviNicheSiteIconName;
  title: string;
  body: string;
}

/**
 * DuviNicheSiteFeatureCard — ported from the bespoke Fishwalkers design
 * system's `components/marketing/FeatureCard.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip): a 44px accent badge
 * holding one glyph, an H3, and a short body, on a hairline card that lifts
 * 1px on hover.
 *
 * The badge is the system's only other permitted transparency — `--accent` at
 * 15%, pre-composed as `--accent-tint-15` because Tailwind cannot alpha a
 * `var()` colour. The glyph is decorative; the card's own text carries the
 * meaning. The export takes a rendered icon node; this takes the glyph name,
 * so the view cannot pass an icon from outside the system's committed set.
 */
export function DuviNicheSiteFeatureCard({
  icon,
  title,
  body,
}: DuviNicheSiteFeatureCardProps) {
  return (
    <article className="flex flex-col items-start gap-[var(--space-4)] rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface-card)] p-[var(--space-8)] shadow-[var(--shadow-card)] transition-[box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-standard)] hover:-translate-y-px hover:shadow-[var(--shadow-card-hover)]">
      <span
        aria-hidden="true"
        className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-[var(--radius-control)] bg-[var(--accent-tint-15)] text-[color:var(--heading)]"
      >
        <DuviNicheSiteIcon name={icon} size={22} />
      </span>
      <h3 className="font-[family-name:var(--font-display)] text-[length:var(--text-h3-sm)] font-bold leading-[var(--leading-heading)] text-[color:var(--heading)] lg:text-[length:var(--text-h3)]">
        {title}
      </h3>
      <p className="m-0 font-[family-name:var(--font-body)] text-[16px] leading-[var(--leading-body)] text-[color:var(--body)]">
        {body}
      </p>
    </article>
  );
}
